<template>
  <div class="auth-page">
    <div class="auth-topbar">
      <div class="auth-brand">
        <b style="font-weight: 800; color: #e8e8e6; font-size: 0.85em; letter-spacing: -0.02em;">turbo</b><span style="font-weight: 300; color: #9ad12b; font-size: 0.85em;">hammer</span>
      </div>
      <modal-help topic="account-register" label="Help for this screen" show-label />
    </div>
    <div class="auth-container">
      <div class="auth-card">
        <template v-if="step === 'details'">
          <h2 class="auth-title">create an account</h2>
          <p class="auth-subtitle">This name becomes the address for the account.</p>
          <form @submit.prevent="continueDetails" class="auth-form">
            <div class="auth-field">
              <label class="auth-label" for="register-invite">invite code</label>
              <input id="register-invite" v-model="inviteCode" type="text" placeholder="invite code" required autocapitalize="none" autocorrect="off" spellcheck="false" autocomplete="off" class="auth-input" />
            </div>
            <div class="auth-field">
              <label class="auth-label" for="register-name">account name</label>
              <input id="register-name" v-model="name" type="text" placeholder="your account name" required maxlength="63" autocapitalize="none" autocorrect="off" spellcheck="false" autocomplete="off" class="auth-input" />
              <p v-if="accountUrl" class="auth-hint"><span>address</span> {{ accountUrl }}</p>
              <p v-else-if="liveNameError" class="auth-error">{{ liveNameError }}</p>
              <p v-else class="auth-hint">Letters, numbers, and hyphens.</p>
            </div>
            <div class="auth-field">
              <label class="auth-label" for="register-email">email</label>
              <input id="register-email" v-model="email" type="email" placeholder="your email address" required autocomplete="email" class="auth-input" />
            </div>
            <div class="auth-field">
              <label class="auth-label" for="register-password">password</label>
              <input id="register-password" v-model="password" type="password" placeholder="your password" required autocomplete="new-password" class="auth-input" />
            </div>
            <div class="auth-field">
              <label class="auth-label" for="register-confirm">confirm password</label>
              <input id="register-confirm" v-model="confirm_password" type="password" placeholder="confirm your password" required autocomplete="new-password" class="auth-input" />
            </div>
            <div v-if="passwordMismatch" class="auth-error">passwords do not match</div>
            <div v-if="errorMessage" class="auth-error">{{ errorMessage }}</div>
            <button type="submit" class="auth-button" :disabled="busy || passwordMismatch">continue</button>
          </form>
        </template>

        <template v-else-if="step === 'choice'">
          <h2 class="auth-title">authenticator app</h2>
          <p class="auth-subtitle">Optional. A code from an authenticator app will be required each time you sign in. You can skip this and turn it on later in account settings.</p>
          <div v-if="errorMessage" class="auth-error">{{ errorMessage }}</div>
          <div class="auth-form">
            <button type="button" class="auth-button" :disabled="busy || creating" @click="beginAuthenticator">set up authenticator</button>
            <button id="skip-authenticator" type="button" class="auth-button auth-button-secondary" :disabled="busy || creating" @click="createAccount()">{{ creating ? 'creating account…' : 'skip for now' }}</button>
            <div v-if="creating" class="signup-progress">
              <div class="signup-progress-track" role="progressbar" :aria-valuenow="creationProgress" aria-valuemin="0" aria-valuemax="100" :aria-valuetext="creationStatus">
                <div class="signup-progress-bar" :style="{ width: creationProgress + '%' }"></div>
              </div>
              <p class="auth-hint">{{ creationProgress }}% · {{ creationStatus }}</p>
            </div>
            <button type="button" class="auth-text-button" :disabled="busy" @click="step = 'details'">back</button>
          </div>
        </template>

        <template v-else-if="step === 'scan'">
          <h2 class="auth-title">set up your authenticator</h2>
          <p class="auth-subtitle">Scan this code with an authenticator app, then enter the 6-digit code it shows.</p>
          <form @submit.prevent="createAccount(true)" class="auth-form">
            <img v-if="qrDataUrl" class="qr-frame" :src="qrDataUrl" alt="Authenticator setup QR code" />
            <p class="auth-hint">Or enter this key</p>
            <p class="secret-key">{{ groupedSecret }}</p>
            <div class="auth-field">
              <label class="auth-label" for="register-code">authenticator code</label>
              <input id="register-code" v-model="authenticatorCode" type="text" inputmode="numeric" autocomplete="one-time-code" spellcheck="false" placeholder="6-digit code" required class="auth-input" />
            </div>
            <div v-if="errorMessage" class="auth-error">{{ errorMessage }}</div>
            <button type="submit" class="auth-button" :disabled="creating">{{ creating ? 'creating account…' : 'verify and create account' }}</button>
            <div v-if="creating" class="signup-progress">
              <div class="signup-progress-track" role="progressbar" :aria-valuenow="creationProgress" aria-valuemin="0" aria-valuemax="100" :aria-valuetext="creationStatus">
                <div class="signup-progress-bar" :style="{ width: creationProgress + '%' }"></div>
              </div>
              <p class="auth-hint">{{ creationProgress }}% · {{ creationStatus }}</p>
            </div>
            <button type="button" class="auth-text-button" :disabled="busy" @click="step = 'choice'">back</button>
          </form>
        </template>

        <template v-else>
          <h2 class="auth-title">save your recovery codes</h2>
          <p class="auth-subtitle">These codes sign you in if you lose the authenticator. Each code works once. This is the only time they are shown.</p>
          <ul class="recovery-codes">
            <li v-for="entry in recoveryCodes" :key="entry">{{ entry }}</li>
          </ul>
          <div class="auth-form">
            <button type="button" class="auth-button auth-button-secondary" @click="copyRecoveryCodes">{{ copyStatus || 'copy codes' }}</button>
            <label class="auth-check" for="codes-saved">
              <input id="codes-saved" v-model="codesSaved" type="checkbox" />
              I saved these codes
            </label>
            <button type="button" class="auth-button" :disabled="!codesSaved" @click="finishSignup">continue</button>
          </div>
        </template>

        <div v-if="step !== 'codes'" class="auth-footer">
          <router-link to="/login" class="auth-link">already have an account? sign in</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

  import axios from 'axios'
  import _ from 'lodash'
  import { accountNameError, normalizeAccountName, tenantBaseUrl } from '../../../superturbodb_manager_server/modules/account-name.js'
  import { takeSignupEvents } from '../../../superturbodb_manager_server/modules/signup-progress.js'
  import { authenticatorQr } from '../lib/authenticator-qr.js'

  export default {
    data () {

      return {
        name: ``,
        inviteCode: ``,
        email: ``,
        password: ``,
        confirm_password: ``,
        errorMessage: ``,
        busy: false,
        creating: false,
        creationStatus: ``,
        creationProgress: 0,
        step: `details`,
        setupToken: ``,
        qrDataUrl: ``,
        manualSecret: ``,
        authenticatorCode: ``,
        recoveryCodes: [],
        redirectUrl: ``,
        codesSaved: false,
        copyStatus: ``
      }
    },
    computed: {
      liveNameError () {

        if (!String(this.name || ``).trim()) {

          return ``
        }

        return accountNameError(this.name)
      },
      accountUrl () {

        if (this.liveNameError) {

          return ``
        }

        const label = normalizeAccountName(this.name)

        if (!label) {

          return ``
        }

        const parent = typeof VUE_APP_TENANT_PARENT_DOMAIN === `undefined` ? `localhost` : VUE_APP_TENANT_PARENT_DOMAIN
        const port = typeof VUE_APP_TENANT_HTTP_PORT === `undefined` ? `8080` : VUE_APP_TENANT_HTTP_PORT

        return tenantBaseUrl(label, parent, port)
      },
      passwordMismatch () {

        return Boolean(this.password && this.confirm_password && this.password !== this.confirm_password)
      },
      groupedSecret () {

        return String(this.manualSecret || ``).replace(/(.{4})/g, `$1 `).trim()
      }
    },
    methods: {
      continueDetails (event) {

        if (!String(this.inviteCode).trim().length || !String(this.name).trim().length || !String(this.email).trim().length || !String(this.password).trim().length || !String(this.confirm_password).trim().length) {

          return event.preventDefault()
        }

        if (this.password !== this.confirm_password) {

          this.errorMessage = `passwords do not match`

          return event.preventDefault()
        }

        const nameProblem = accountNameError(this.name)

        if (nameProblem) {

          this.errorMessage = this.liveNameError ? `` : nameProblem

          return event.preventDefault()
        }

        this.errorMessage = ``
        this.step = `choice`
      },
      async beginAuthenticator () {

        this.errorMessage = ``
        this.busy = true

        try {

          const response = await axios.post(`${VUE_APP_MANAGER_SERVER_URL}/account/two-factor/setup`, {
            email: this.email
          })

          this.setupToken = response.data.setupToken
          this.manualSecret = response.data.secret
          this.authenticatorCode = ``

          try {

            this.qrDataUrl = await authenticatorQr(response.data.otpauthUrl)
          } catch (qrError) {

            this.qrDataUrl = ``
          }

          this.step = `scan`
        } catch (error) {

          this.errorMessage = this.messageFrom(error, `could not start authenticator setup`)
        } finally {

          this.busy = false
        }
      },
      async createAccount (withAuthenticator) {

        this.errorMessage = ``
        this.creating = true
        this.creationStatus = `Creating your account`
        this.creationProgress = 8

        const body = {
          name: normalizeAccountName(this.name),
          email: this.email,
          password: this.password,
          inviteCode: this.inviteCode
        }

        if (withAuthenticator) {

          body.setupToken = this.setupToken
          body.code = this.authenticatorCode
        }

        try {

          const result = await this.readSignup(body)

          if (result.recoveryCodes && result.recoveryCodes.length) {

            this.recoveryCodes = result.recoveryCodes
            this.redirectUrl = result.redirectUrl
            this.codesSaved = false
            this.copyStatus = ``
            this.step = `codes`
            this.creating = false

            return
          }

          window.location.assign(result.redirectUrl)
        } catch (error) {

          console.log(`error creating account:`)
          console.log(error)
          this.errorMessage = error.signupMessage || `something went wrong creating your account — please try again`
          this.creating = false
        }
      },
      async readSignup (body) {

        const response = await fetch(`${VUE_APP_MANAGER_SERVER_URL}/account/`, {
          method: `POST`,
          headers: {
            'Content-Type': `application/json`,
            Accept: `text/event-stream`
          },
          body: JSON.stringify(body)
        })
        const type = response.headers.get(`content-type`) || ``

        if (type.indexOf(`text/event-stream`) === -1) {

          const message = await response.text()

          throw Object.assign(new Error(message), { signupMessage: message || `could not create the account` })
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ``
        let result = null

        while (true) {

          const chunk = await reader.read()

          if (chunk.done) break

          buffer += decoder.decode(chunk.value, { stream: true })
          const parsed = takeSignupEvents(buffer)

          buffer = parsed.rest
          parsed.events.forEach((event) => {

            if (event.status) this.creationStatus = event.status
            if (typeof event.progress === `number`) this.creationProgress = event.progress
            if (event.error) throw Object.assign(new Error(event.error), { signupMessage: event.error })
            if (event.redirectUrl) result = event
          })
        }

        if (!result || !result.redirectUrl) {

          throw Object.assign(new Error(`could not create the account`), { signupMessage: `could not create the account` })
        }

        return result
      },
      finishSignup () {

        if (!this.codesSaved || !this.redirectUrl) {

          return
        }

        window.location.assign(this.redirectUrl)
      },
      async copyRecoveryCodes () {

        try {

          await navigator.clipboard.writeText(this.recoveryCodes.join(`\n`))
          this.copyStatus = `copied`
        } catch (error) {

          this.copyStatus = `select the codes and copy them`
        }
      },
      messageFrom (error, fallback) {

        const data = error.response && error.response.data

        if (typeof data === `string` && data) {

          return data
        }

        return fallback
      }
    },
    components: {
    }
  }
</script>

<style scoped>

.auth-page {
  min-height: 100vh;
  background-color: #181916;
  font-family: 'Helvetica Neue', 'Avenir', Helvetica, Arial, sans-serif;
}

.auth-topbar {
  display: flex;
  align-items: center;
  height: 40px;
  background-color: #0f0f0d;
  border-bottom: 1px solid #2a2a27;
  padding: 0 12px;
}

.auth-brand {
  flex-shrink: 0;
}

.auth-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 40px);
  padding: 40px 20px;
}

.auth-card {
  width: 100%;
  max-width: 400px;
  background-color: #1e1e1b;
  border: 1px solid #2a2a27;
  border-radius: 8px;
  padding: 40px 36px;
}

.auth-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #e8e8e6;
  margin: 0 0 6px 0;
}

.auth-subtitle {
  font-size: 0.82rem;
  color: #707070;
  margin: 0 0 32px 0;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.auth-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.auth-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #9ad12b;
  text-transform: lowercase;
  letter-spacing: 0.03em;
}

.auth-input {
  background-color: #141412;
  border: 1px solid #2a2a27;
  border-radius: 5px;
  padding: 10px 12px;
  font-size: 0.85rem;
  color: #e8e8e6;
  outline: none;
  transition: border-color 0.15s;
  font-family: inherit;
}

.auth-input::placeholder {
  color: #4a4a47;
}

.auth-input:focus {
  border-color: #9ad12b;
}

.auth-button {
  margin-top: 8px;
  padding: 10px 0;
  background-color: #9ad12b;
  color: #0f0f0d;
  border: none;
  border-radius: 5px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;
  font-family: inherit;
}

.auth-button:hover:not(:disabled) {
  background-color: #b8f046;
}

.auth-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.auth-button-secondary {
  background-color: transparent;
  color: #e8e8e6;
  border: 1px solid #3a3a36;
}

.auth-button-secondary:hover:not(:disabled) {
  border-color: #9ad12b;
  color: #9ad12b;
  background-color: transparent;
}

.auth-text-button {
  padding: 0;
  background: none;
  border: none;
  color: #707070;
  font-size: 0.78rem;
  cursor: pointer;
  font-family: inherit;
}

.auth-text-button:hover:not(:disabled) {
  color: #9ad12b;
}

.qr-frame {
  width: 180px;
  height: 180px;
  align-self: center;
  background: #fff;
  padding: 8px;
  border-radius: 6px;
}

.secret-key {
  margin: 0;
  color: #e8e8e6;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
  letter-spacing: 0.04em;
}

.recovery-codes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
  margin: 0 0 20px 0;
  padding: 0;
  list-style: none;
  color: #e8e8e6;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
}

.auth-check {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #c0c0be;
  font-size: 0.8rem;
}

.auth-check input {
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: #9ad12b;
}

.auth-hint span {
  margin-right: 6px;
}

.auth-error {
  color: #e05252;
  font-size: 0.78rem;
}

.auth-hint {
  color: #707070;
  font-size: 0.75rem;
  margin: 0;
  word-break: break-all;
}

.signup-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.signup-progress-track {
  height: 8px;
  border-radius: 999px;
  background: #2a2a28;
  overflow: hidden;
}

.signup-progress-bar {
  height: 100%;
  border-radius: 999px;
  background: #9ad12b;
  transition: width 0.4s ease;
}

.auth-footer {
  margin-top: 24px;
  text-align: center;
}

.auth-link {
  color: #707070;
  font-size: 0.78rem;
  text-decoration: none;
  transition: color 0.15s;
}

.auth-link:hover {
  color: #9ad12b;
}

</style>
